<template>
  <div>
    <div
      v-if="!hasSentAReview"
      class="spa-review-form"
    >
      <div class="spa-review-form__userinfo">
        <div class="spa-review-form__userinfo__input-wrapper">
          <SplInputBox
            v-model="review.author.name"
            class="spa-review-form__userinfo__name"
            :label="nameLabel"
            type="text"
          />
        </div>
        <div class="spa-review-form__userinfo__input-wrapper">
          <SplInputBox
            v-model="review.author.email"
            class="spa-review-form__userinfo__email"
            :label="emailLabel"
            type="email"
          />
        </div>
      </div>

      <SplInputArea
        v-model="review.content"
        :placeholder="commentLabel"
      />

      <div class="spa-review-form__scores">
        <SpaReviewScoreSelector
          v-model="review.scores.environment"
          :label="environmentLabel"
        />
        <SpaReviewScoreSelector
          v-model="review.scores.service"
          :label="serviceLabel"
        />
        <SpaReviewScoreSelector
          v-model="review.scores.customerService"
          :label="attentionLabel"
        />
        <SpaReviewScoreSelector
          v-model="review.scores.valueForMoney"
          :label="qualityPriceLabel"
        />
      </div>

      <div class="spa-review-form__recaptcha">
        <VueRecaptcha
          :sitekey="recaptchaKey"
          @verify="recaptchaSuccessCallback"
        />
      </div>

      <Loading
        v-if="loading"
        :is-displayed="loading"
      />

      <div class="spa-review-form__submit">
        <SplButton
          variant="acent"
          :disabled="isSubmitBtnDisabled || loading"
          @click="saveReview"
        >
          <Lang
            by-key="send"
            capitalize-first
          />
        </SplButton>
      </div>
    </div>
    <div
      v-else
      class="spa-review-form__sent"
    >
      <Lang
        by-key="sent-comment"
        capitalize-first
      />
    </div>
  </div>
</template>

<script lang="ts">
import VueRecaptcha from 'vue3-recaptcha2'

import SplInputBox from '../../shared/form/SplInputBox.vue'
import SplInputArea from '../../shared/form/SplInputArea.vue'
import SplButton from  '../../shared/form/SplButton.vue'
import SpaReviewScoreSelector from '../atom/SpaReviewScoreSelector.vue'

import { SpaReview } from '~/core/ts/entity/SpaReview'

import SpaReviewAPIRepository from '@/core/ts/repository/SpaReviewAPIRepository'
import LoadingEventBus from '~/core/events/loading'

import { translateByKey } from '~/core/ts/util/translate'
import { capitalizeFirstWord } from '~/core/ts/util/string'

export default defineNuxtComponent({
  components: {
    SplInputBox,
    SplInputArea,
    SplButton,
    SpaReviewScoreSelector,
    VueRecaptcha,
  },
  props: {
    spaUuid: { type: String, required: true }
  },
  setup(props) {
    const review = ref(SpaReview.createEmpty(props.spaUuid))
    const loading = ref(false)
    const hasSentAReview = ref(false)

    const runtimeConfig = useRuntimeConfig()
    const recaptchaKey  = runtimeConfig.public.recaptchaSiteKey || ''

    async function saveReview() {
      LoadingEventBus.show()
      await SpaReviewAPIRepository.publishReview(review.value)
      LoadingEventBus.hide()
      hasSentAReview.value = true
    }

    const isRecaptchaValid = ref(false)
    function recaptchaSuccessCallback() {
      isRecaptchaValid.value = (true)
    }

    const nameLabel = capitalizeFirstWord(translateByKey('name')) + ' *'
    const emailLabel = capitalizeFirstWord(translateByKey('email')) + ' *'
    const commentLabel = capitalizeFirstWord(translateByKey('comment')) + ' *'

    const environmentLabel = capitalizeFirstWord(translateByKey('environment'))
    const serviceLabel = capitalizeFirstWord(translateByKey('service'))
    const attentionLabel = capitalizeFirstWord(translateByKey('attention'))
    const qualityPriceLabel = capitalizeFirstWord(translateByKey('quality-price'))

    const isSubmitBtnDisabled = computed(() => {
      const authorName = review.value.author.name.trim()
      if (!authorName) {
        return true
      }

      const authorEmail = review.value.author.email.trim()
      if (!authorEmail) {
        return true
      }

      const content = review.value.content.trim()
      if (!content) {
        return true
      }

      if (!isRecaptchaValid.value) {
        return true
      }

      return false
    })

    return {
      nameLabel,
      emailLabel,
      commentLabel,

      environmentLabel,
      serviceLabel,
      attentionLabel,
      qualityPriceLabel,

      review,
      loading,
      hasSentAReview,
      isRecaptchaValid,

      isSubmitBtnDisabled,

      recaptchaKey,
      recaptchaSuccessCallback,
      saveReview,
    }
  },
})
</script>

<style lang="scss" scoped>
.spa-review-form {
  @apply py-4;
  @apply px-2;

  &__userinfo {
    // &__name,
    // &__email {
    // }
    @screen lg {
      @apply grid;
      @apply grid-cols-2;
      @apply gap-x-4;
      @apply gap-y-4;
    }

    &__input-wrapper {
      @apply border-1;
    }
  }

  &__scores {
    @apply my-4;

    @screen lg {
      @apply gap-x-8;
      @apply grid;
      @apply grid-cols-2;
    }
  }

  &__submit {
    @apply my-4;

    button {
      @apply py-5;
      @apply px-7;
      border: 1px solid black;
    }
  }

  &__sent {
    @apply bg-green-100;
    @apply p-3;
    @apply my-4;

    @apply border-2;
    @apply border-green-300;
    @apply rounded-lg;
  }
}
</style>
